<template>
    <div class="sl-wrap">
        <div class="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
                <h6 class="font-semibold mb-1">Liens courts (QR Scan &amp; Go)</h6>
                <p class="text-[0.8rem] text-defaulttextcolor/70 mb-0">
                    Liens raccourcis encodés dans les QR (panier, menu, enrôlement serveur).
                    Panier/menu sont permanents ; les liens serveur expirent chaque matin à 9h.
                </p>
            </div>
            <div class="flex items-center gap-2">
                <span class="badge bg-primary/10 text-primary text-[0.8rem]">
                    {{ liens.length }} lien(s)
                </span>
                <button class="ti-btn ti-btn-primary-full ti-btn-sm" :disabled="loading" @click="charger">
                    <i class="ri-refresh-line" :class="{ 'animate-spin': loading }"></i>
                    <span>Actualiser</span>
                </button>
            </div>
        </div>

        <div v-if="loading && !loaded" class="text-center py-6 text-defaulttextcolor/60">
            <i class="ri-loader-4-line animate-spin text-2xl"></i>
            <p class="mb-0 mt-2">Chargement…</p>
        </div>

        <div v-else-if="erreur" class="alert alert-danger" role="alert">
            <i class="ri-error-warning-line me-1"></i> {{ erreur }}
        </div>

        <div v-else-if="loaded && !liens.length" class="text-center py-6 text-defaulttextcolor/60">
            <i class="ri-link-unlink text-2xl"></i>
            <p class="mb-0 mt-2">Aucun lien court pour le moment.</p>
        </div>

        <div v-else-if="loaded" class="table-responsive">
            <table class="table table-hover whitespace-nowrap min-w-full">
                <thead>
                    <tr>
                        <th class="text-start">Type</th>
                        <th class="text-start">Code</th>
                        <th class="text-start">Cible</th>
                        <th class="text-start">Expiration</th>
                        <th class="text-start">Statut</th>
                        <th class="text-start">Créé le</th>
                        <th class="text-end">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="l in liens" :key="l.id">
                        <td>
                            <span class="badge" :class="badgeType(l.type)">{{ libelleType(l.type) }}</span>
                        </td>
                        <td class="font-mono text-[0.8rem]">{{ l.code }}</td>
                        <td class="font-mono text-[0.72rem] text-defaulttextcolor/60">{{ tronquer(l.cible) }}</td>
                        <td class="text-[0.8rem]">
                            <span v-if="estPermanent(l)" class="badge bg-success/10 text-success">
                                <i class="ri-infinity-line me-1"></i>Permanent
                            </span>
                            <span v-else-if="estExpire(l)" class="badge bg-danger/10 text-danger">
                                <i class="ri-time-line me-1"></i>Expiré
                            </span>
                            <span v-else class="text-warning">
                                <i class="ri-time-line me-1"></i>{{ formatDateTime(l.expire_at) }}
                            </span>
                        </td>
                        <td>
                            <span class="badge" :class="estActif(l) ? 'bg-success/10 text-success' : 'bg-secondary/10 text-secondary'">
                                <i class="ri-circle-fill text-[0.5rem] me-1"></i>
                                {{ estActif(l) ? 'Actif' : 'Désactivé' }}
                            </span>
                        </td>
                        <td class="text-[0.8rem]">{{ formatDateTime(l.created_at) }}</td>
                        <td class="text-end">
                            <button class="ti-btn ti-btn-sm !py-1 !px-2 me-1"
                                    :class="estActif(l) ? 'ti-btn-warning-full' : 'ti-btn-success-full'"
                                    :disabled="busyId === l.id"
                                    @click="basculer(l)">
                                <i :class="estActif(l) ? 'ri-pause-circle-line' : 'ri-play-circle-line'"></i>
                                <span>{{ estActif(l) ? 'Désactiver' : 'Réactiver' }}</span>
                            </button>
                            <button class="ti-btn ti-btn-sm ti-btn-danger-full !py-1 !px-2"
                                    :disabled="busyId === l.id"
                                    @click="supprimer(l)">
                                <i class="ri-delete-bin-line"></i>
                                <span>Supprimer</span>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
    /* eslint-disable */
    const axios = require('axios');
    import * as JwtService from "@/common/jwt.service";
    import { randomString } from "@/allfunction";

    /**
     * Onglet Paramètres - gestion des liens courts du raccourcisseur de QR.
     *
     * Lit/écrit les routes PROTÉGÉES scango shortlinklist / shortlinkactif /
     * shortlinkdelete (btkpid de la session : un tenant ne voit et ne gère que ses
     * propres liens). Données : scango_shortlinks.
     */
    export default {
        name: "shortlinks",
        data: () => ({
            loading: false,
            loaded: false,
            erreur: "",
            liens: [],
            busyId: null,
        }),
        methods: {
            _headers() {
                return {
                    "X-Requested-With": "XMLHttpRequest",
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                    "Authorization": "Bearer " + JwtService.getToken(),
                    "csrftoken": this.$store.state.appsociete,
                };
            },

            _url(action) {
                return process.env.VUE_APP_API_URL
                    + "/egl745874pscango771714dfe/caisse/othetdskd8755dsdhisd/" + action + "/"
                    + randomString();
            },

            libelleType(t) {
                if (t === "panier") return "Panier";
                if (t === "menu") return "Menu";
                if (t === "serveur") return "Serveur";
                return t || "-";
            },
            badgeType(t) {
                if (t === "panier") return "bg-primary/10 text-primary";
                if (t === "menu") return "bg-info/10 text-info";
                if (t === "serveur") return "bg-purple/10 text-purple";
                return "bg-light text-defaulttextcolor";
            },

            // expire_at NULL → permanent (le SQL renvoie permanent=1)
            estPermanent(l) { return Number(l.permanent) === 1 || l.expire_at == null; },
            estExpire(l) { return Number(l.perime) === 1; },
            estActif(l) { return Number(l.actif) === 1; },

            tronquer(c) {
                const s = String(c || "");
                return s.length > 42 ? s.slice(0, 42) + "…" : s;
            },

            formatDateTime(v) {
                if (!v) return "-";
                const d = new Date(v);
                if (isNaN(d.getTime())) return String(v);
                const p = (n) => String(n).padStart(2, "0");
                return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
            },

            async charger() {
                this.loading = true;
                this.erreur = "";
                try {
                    const response = await axios.post(this._url("shortlinklist"), {}, { headers: this._headers(), withCredentials: true });
                    const d = response.data || {};
                    if (d.success && d.m) {
                        this.liens = Array.isArray(d.m.liens) ? d.m.liens : [];
                    } else {
                        this.erreur = "Accès refusé ou module Scan & Go non configuré.";
                    }
                    this.loaded = true;
                } catch (e) {
                    console.error("[shortlinks] chargement:", e);
                    this.erreur = "Impossible de charger les liens courts.";
                    this.loaded = true;
                } finally {
                    this.loading = false;
                }
            },

            async basculer(l) {
                this.busyId = l.id;
                try {
                    const cible = !this.estActif(l);
                    const response = await axios.post(this._url("shortlinkactif"),
                        { id: l.id, actif: cible },
                        { headers: this._headers(), withCredentials: true });
                    if (response.data && response.data.success) {
                        l.actif = cible ? 1 : 0;
                        if (window.toastr) window.toastr.success(cible ? "Lien réactivé." : "Lien désactivé.", "<b></b>");
                    } else {
                        if (window.toastr) window.toastr.error("Action refusée.", "<b></b>");
                    }
                } catch (e) {
                    console.error("[shortlinks] bascule:", e);
                    if (window.toastr) window.toastr.error("Échec de l'action.", "<b></b>");
                } finally {
                    this.busyId = null;
                }
            },

            async supprimer(l) {
                const ok = window.confirm("Supprimer ce lien court ? Ce QR imprimé cessera de fonctionner.");
                if (!ok) return;
                this.busyId = l.id;
                try {
                    const response = await axios.post(this._url("shortlinkdelete"),
                        { id: l.id },
                        { headers: this._headers(), withCredentials: true });
                    if (response.data && response.data.success) {
                        this.liens = this.liens.filter((x) => x.id !== l.id);
                        if (window.toastr) window.toastr.success("Lien supprimé.", "<b></b>");
                    } else {
                        if (window.toastr) window.toastr.error("Suppression refusée.", "<b></b>");
                    }
                } catch (e) {
                    console.error("[shortlinks] suppression:", e);
                    if (window.toastr) window.toastr.error("Échec de la suppression.", "<b></b>");
                } finally {
                    this.busyId = null;
                }
            },
        },
    };
</script>

<style scoped>
    .sl-wrap { padding: 0.25rem; }
</style>
